import { useState, useRef, useCallback, FormEvent, useEffect } from 'react';
import { useAuth } from '@/auth/auth-provider';
import { getHostName } from '@/tools/office-tools-registry';
import { DatadogUserProvider } from '@/components/DatadogUserProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, AlignLeftIcon, SquarePen } from 'lucide-react';
import { ChatInterface } from './ChatInterface';
import { SettingsPanel } from '@/components/settings/SettingsPanel';
import { ConnectorList } from '@/components/connectors/ConnectorList';
import { AgentSelectionPage } from '@/components/agents/AgentSelectionPage';
import { AgentDialog } from '@/components/agents/AgentDialog';
import { SkillList } from '@/components/skills/SkillList';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useWorkspace } from '@/hooks/use-workspace';
import { getAppConfig } from '@/config/app-config';
import { useAgents } from '@/hooks/use-agents';
import { useDefaultAgent } from '@/hooks/use-default-agent';
import { useSkills } from '@/hooks/use-skills';
import type { Agent } from '@/types/agent.types';

type View = 'agent-selection' | 'agent-editor' | 'chat' | 'skills' | 'settings' | 'connectors';

export function TaskPane() {
  const { user, signInWithPassword, signInSilentSSO, signOut, isAuthenticated, loading: authLoading, error: authError } = useAuth();

  if (authLoading) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <Loader2 className="size-6 text-primary animate-spin" />
          <p className="text-sm text-muted-foreground">Signing in...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <Header />
        <LoginForm
          onSignInWithPassword={signInWithPassword}
          onSignInSilentSSO={signInSilentSSO}
          loading={authLoading}
          error={authError}
        />
      </div>
    );
  }

  return <AuthenticatedApp user={user} onSignOut={signOut} />;
}

function AuthenticatedApp({
  user,
  onSignOut,
}: {
  user: { name: string; email: string; avatar?: string } | null;
  onSignOut: () => Promise<void>;
}) {
  const { enableSettingsPage } = getAppConfig();
  const { workspace, loading: wsLoading, error: wsError } = useWorkspace();
  const workspaceId = workspace?.id ?? null;
  const { defaultAgent, loading: defaultAgentLoading, error: defaultAgentError, retry: retryDefaultAgent } = useDefaultAgent(
    workspaceId,
    getHostName(),
  );

  const [view, setView] = useState<View>('chat');
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [dialogAgent, setDialogAgent] = useState<Agent | null | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ type: 'switch-session' | 'new-chat'; sessionId?: string } | null>(null);
  const { createAgent, updateAgent } = useAgents(workspaceId, false);
  const cancelChatRef = useRef<(() => void | Promise<void>) | null>(null);

  const handleRegisterCancel = useCallback((cancelFn: () => void) => {
    cancelChatRef.current = cancelFn;
  }, []);

  useEffect(() => {
    if (!enableSettingsPage && view === 'settings') {
      setView('chat');
    }
  }, [enableSettingsPage, view]);

  // Use the default agent as selectedAgent when none is explicitly chosen
  const effectiveAgent = selectedAgent ?? defaultAgent;

  // Show loading while workspace and default agent are being resolved
  if (wsLoading || defaultAgentLoading) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <Header user={user} />
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <Loader2 className="size-6 text-primary animate-spin" />
          <p className="text-sm text-muted-foreground">Setting up {getHostName()} workspace...</p>
        </div>
      </div>
    );
  }

  if (wsError) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <Header user={user} />
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
          <p className="text-sm text-destructive text-center">{wsError}</p>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (defaultAgentError) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <Header user={user} />
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
          <p className="text-sm text-destructive text-center">{defaultAgentError}</p>
          <Button variant="outline" size="sm" onClick={retryDefaultAgent}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const handleAgentSelect = (agent: Agent) => {
    setSelectedAgent(agent);
    setSessionId(null);
    setView('chat');
  };

  const handleSessionSelect = (sid: string, _agentId?: string | null) => {
    if (sessionId) {
      setPendingAction({ type: 'switch-session', sessionId: sid });
      setShowLeaveConfirm(true);
    } else {
      setSessionId(sid);
      setView('chat');
    }
  };

  const handleGlobalNewChat = () => {
    if (sessionId) {
      setPendingAction({ type: 'new-chat' });
      setShowLeaveConfirm(true);
    } else {
      setSessionId(null);
      setView('chat');
    }
  };

  const handleNavigate = (nextView: View) => {
    if (nextView === 'settings' && !enableSettingsPage) return;
    if (nextView === 'agent-selection') {
      cancelChatRef.current?.();
      cancelChatRef.current = null;
      setSelectedAgent(null);
      setSessionId(null);
    }
    setView(nextView);
  };


  const handleCreateAgent = () => {
    setDialogAgent(null);
    setView('agent-editor');
  };

  const handleEditAgent = (agent: Agent) => {
    setDialogAgent(agent);
    setView('agent-editor');
  };

  const handleSaveAgent = async (data: any) => {
    if (dialogAgent === null) {
      return await createAgent(data);
    } else if (dialogAgent) {
      return await updateAgent(dialogAgent.id, data);
    }
    throw new Error('Invalid dialog state');
  };


  return (
    <DatadogUserProvider>
    <div className="flex flex-col h-screen bg-background relative overflow-hidden">
      {/* Gradient overlay from top fading smoothly downward */}
      <div className="absolute top-0 left-0 right-0 h-1/3 pointer-events-none z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.12] via-primary/[0.08] via-15% via-primary/[0.04] via-transparent via-transparent via-transparent to-transparent" />
      </div>
      
        <AppSidebar
          open={sidebarOpen}
          currentView={view}
          currentSessionId={sessionId}
          workspaceId={workspaceId}
          user={user}
          onClose={() => setSidebarOpen(false)}
          onNavigate={(nextView) => {
            handleNavigate(nextView);
            setSidebarOpen(false);
          }}
        onSessionSelect={(sid) => {
          handleSessionSelect(sid);
          setSidebarOpen(false);
        }}
        onNewChat={() => {
          handleGlobalNewChat();
          setSidebarOpen(false);
        }}
        onSignOut={onSignOut}
      />

      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/20"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close sidebar"
        />
      )}
      <Header
        user={user}
        onToggleSidebar={() => setSidebarOpen(true)}
        onNewChat={handleGlobalNewChat}
      />
      {/* Hidden for now — re-enable when ready
      {view === 'agent-selection' && (
        <AgentSelectionPage
          key={refreshKey}
          workspaceId={workspaceId}
          onAgentSelect={handleAgentSelect}
          onSessionSelect={handleSessionSelect}
          onCreateAgent={handleCreateAgent}
          onEditAgent={handleEditAgent}
        />
      )}
      */}
      {view === 'chat' && (
        <ChatInterface
          selectedAgentId={effectiveAgent?.id}
          initialSessionId={sessionId}
          workspaceId={workspaceId}
          onRegisterCancel={handleRegisterCancel}
          onSessionCreated={setSessionId}
          agentRefreshKey={refreshKey}
        />
      )}
      {/* Hidden for now — re-enable when ready
      {view === 'skills' && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <SkillList workspaceId={workspaceId} />
        </div>
      )}
      {view === 'settings' && (
        <div className="flex-1 min-h-0">
          <SettingsPanel workspaceId={workspaceId} />
        </div>
      )}
      {view === 'connectors' && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <ConnectorList workspaceId={workspaceId} />
        </div>
      )}
      {view === 'agent-editor' && dialogAgent !== undefined && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <AgentDialog
            agent={dialogAgent}
            workspaceId={workspaceId}
            variant="panel"
            onClose={() => {
              setDialogAgent(undefined);
              setRefreshKey((prev) => prev + 1);
              setView('agent-selection');
            }}
            onSave={handleSaveAgent}
          />
        </div>
      )}
      */}
      
      <ConfirmationDialog
        open={showLeaveConfirm}
        title="Switch chat?"
        description="Switching the chat will interrupt the current process."
        confirmLabel="Switch"
        cancelLabel="Stay"
        variant="default"
        onConfirm={() => {
          cancelChatRef.current?.();
          cancelChatRef.current = null;
          if (pendingAction?.type === 'switch-session' && pendingAction.sessionId) {
            setSessionId(pendingAction.sessionId);
          } else if (pendingAction?.type === 'new-chat') {
            setSessionId(null);
          }
          setView('chat');
          setShowLeaveConfirm(false);
          setPendingAction(null);
        }}
        onCancel={() => {
          setShowLeaveConfirm(false);
          setPendingAction(null);
        }}
      />
    </div>
    </DatadogUserProvider>
  );
}

function Header({
  user,
  onToggleSidebar,
  onNewChat,
}: {
  user?: { name: string; email: string; avatar?: string } | null;
  onToggleSidebar?: () => void;
  onNewChat?: () => void;
}) {
  return (
    <div className="relative flex items-center justify-between px-3 py-2 bg-transparent shrink-0 z-20">
      <div className="flex items-center gap-2">
        <button
          onClick={onToggleSidebar}
          className="hover:bg-accent/50 rounded p-1 transition-colors"
          title="Open menu"
        >
          <AlignLeftIcon className="size-5 text-muted-foreground" strokeWidth={2.2} />
        </button>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {user && onNewChat && (
          <button
            onClick={onNewChat}
            title="New chat"
            aria-label="New chat"
            className="flex items-center justify-center size-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-foreground/10 transition-colors"
          >
            <SquarePen className="size-4" strokeWidth={1.8} />
          </button>
        )}
      </div>
    </div>
  );
}

function LoginForm({
  onSignInWithPassword,
  onSignInSilentSSO,
  loading,
  error,
}: {
  onSignInWithPassword: (email: string, password: string) => Promise<void>;
  onSignInSilentSSO: () => Promise<void>;
  loading: boolean;
  error: string | null;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [silentSsoLoading, setSilentSsoLoading] = useState(false);

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!email || !password) {
      setLocalError('Please enter email and password');
      return;
    }
    try {
      await onSignInWithPassword(email, password);
    } catch {
      // error is set by auth provider
    }
  };

  const handleSilentSSO = async () => {
    setLocalError(null);
    setSilentSsoLoading(true);
    try {
      await onSignInSilentSSO();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Silent SSO failed';
      setLocalError(message);
    } finally {
      setSilentSsoLoading(false);
    }
  };

  const displayError = localError || error;

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 gap-4">
      <div className="text-center">
        <h2 className="text-base font-semibold">Welcome to AI Studio</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Sign in to start using AI assistants with {getHostName()}
        </p>
      </div>

      <div className="w-full max-w-xs flex flex-col gap-3">
        <Button
          type="button"
          variant="default"
          disabled={loading || silentSsoLoading}
          className="w-full"
          onClick={handleSilentSSO}
        >
          {silentSsoLoading ? (
            <Loader2 className="size-4 animate-spin mr-2" />
          ) : (
            <svg
              viewBox="0 0 23 23"
              xmlns="http://www.w3.org/2000/svg"
              className="size-4 mr-2"
              aria-hidden="true"
            >
              <path d="M0 0h11v11H0V0z" fill="#F25022" />
              <path d="M12 0h11v11H12V0z" fill="#7FBA00" />
              <path d="M0 12h11v11H0V12z" fill="#00A4EF" />
              <path d="M12 12h11v11H12V12z" fill="#FFB900" />
            </svg>
          )}
          Sign in with Microsoft
        </Button>

        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-border" />
          <span className="text-[11px] text-muted-foreground">or</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-3">
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoComplete="email"
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoComplete="current-password"
          />

          {displayError && (
            <p className="text-xs text-destructive text-center">{displayError}</p>
          )}

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
            Sign In with Email
          </Button>
        </form>
      </div>
    </div>
  );
}
